import React, { useEffect, useRef, useState} from 'react'
import useDebounce from '../../../../hooks/useDebounce';
import { ICountrySelectDropdownHook } from '../../../../interfaces/textInputs/phoneNumberInputs';




const useCountrySelectDropdown = ({defaultValue, options, setValue, id, canDeselect}: ICountrySelectDropdownHook) => {
    const dropdownRef = useRef<any>(undefined);

    const [showDropdown, setShowDropdown] = useState<boolean>(false);
    const [activeElement, setActiveElement] = useState<string>('');
    const [selectedElement, setSelectedElement] = useState<string>(defaultValue);
    const [focusIndex, setFocusIndex] = useState<number>(0);
    const [typeAhead, setTypeAhead] = useState<string>('');

    const debouncedString = useDebounce(typeAhead, 500)

    useEffect(() => {
        if (debouncedString) {
            const searchIndex = options.findIndex(el => el.label.split(' ')[1].toLowerCase().startsWith(debouncedString.toLowerCase()))
            if (searchIndex > 0) {
                setFocusIndex(searchIndex)
            }
        }
        setTypeAhead('');
    }, [debouncedString])

    useEffect(() => {
        function handleClickOutside(event: Event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        setValue(selectedElement)
    }, [selectedElement]);

    useEffect(() => {
        setSelectedElement(defaultValue)
    }, [defaultValue]);

    useEffect(() => {
        if (!showDropdown) {
            setActiveElement(``)
            setFocusIndex(0);
        }
    }, [showDropdown]);

    useEffect(() => {
        if (showDropdown) {
            setAndFocusNextElement(getElementName(focusIndex))
        }
    }, [focusIndex, showDropdown]);

    function handleKeyPressOnButton(e: React.KeyboardEvent<HTMLButtonElement>) {
        switch (e.code) {
            case 'ArrowDown':
            case 'ArrowUp':
            case 'Space':
            case 'Enter':
            case 'Home':
            case 'End':
                e.preventDefault();
                setShowDropdown(!showDropdown);
                setFocusIndex(e.code === 'End' ? options.length - 1 : 0);
                break;
            case 'Tab':
                setShowDropdown(false);
                document.getElementById(`${id}-open-menu-button`)?.focus();
                break;
        }
    }

    function handleKeyPressInList(e: React.KeyboardEvent<HTMLDivElement>) {
        e.preventDefault();
        switch (e.code) {
            case 'Tab':
                setSelectedElement((canDeselect && `${options[focusIndex].value}` === selectedElement) ? '' : `${options[focusIndex].value}`);
                setShowDropdown(false);
                document.getElementById(`${id}-open-menu-button`)?.focus();
                break;
            case 'Enter':
            case 'Space':
                setSelectedElement((canDeselect && `${options[focusIndex].value}` === selectedElement) ? '' : `${options[focusIndex].value}`);
                setShowDropdown(false);
                document.getElementById(`${id}-open-menu-button`)?.focus();
                break;
            case 'ArrowDown':
                e.preventDefault();
                setFocusIndex(focusIndex + 1 >= options.length ? 0 : focusIndex + 1);
                break;
            case 'ArrowUp':
                e.preventDefault();
                setFocusIndex(focusIndex - 1 < 0 ? options.length - 1 : focusIndex - 1);
                break;
            case 'Home':
                e.preventDefault();
                setFocusIndex(0);
                break;
            case 'End':
                e.preventDefault();
                setFocusIndex(options.length - 1);
                break;
            case 'Esc':
                setShowDropdown(false);
                document.getElementById(`${id}-open-menu-button`)?.focus();
                break;
            default:
                setTypeAhead(typeAhead + e.key)
        }
    }

    function setAndFocusNextElement(elementId: string) {
        if (!elementId) { return; }

        const element = document.getElementById(elementId)

        if (element) {
            element.focus();
        }
    }

    function getElementName(index: number) {
        return `${id}-option-${index}`
    }

    return {
        dropdownRef,
        setShowDropdown,
        showDropdown,
        activeElement,
        handleKeyPressOnButton,
        selectedElement,
        setActiveElement, 
        setSelectedElement,
        handleKeyPressInList
    }
}

export default useCountrySelectDropdown